import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    Cell,
    Label,
} from "recharts";
import { theme } from "../theme";
import SkeletonCard from "./common/SkeletonCard";
import EmptyState from "./common/EmptyState";
import SummaryCard from "./common/SummaryCard";
import DashboardCard from "./common/DashboardCard";

import { API_BASE } from '../api/config';

function formatCurrency(value, displayCurrency = "AED") {
    if (value == null) return "-";
    return `${displayCurrency} ${Math.round(Number(value)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function ClosedLostReasons({ filterContext, onMaximize, fullScreen = false }) {
    const { salesperson, year, region, displayCurrency = "AED" } = filterContext || {};
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: "lost_count", direction: "desc" });

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            const params = {
                salesperson,
                year,
                region: region || "all",
            };

            const res = await axios.get(`${API_BASE}/api/closed-lost/reasons`, { params });
            setData(res.data || []);
        } catch (err) {
            console.error("ClosedLostReasons error", err);
            const errorMessage = err.response?.data?.detail || err.message || "Failed to load closed lost reasons";
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [salesperson, year, region]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const sortedData = useMemo(() => {
        return [...data].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];

            if (sortConfig.key === "lost_count" || sortConfig.key === "total_lost_value") {
                return sortConfig.direction === "asc"
                    ? (aVal || 0) - (bVal || 0)
                    : (bVal || 0) - (aVal || 0);
            }

            const aStr = String(aVal || "").toLowerCase();
            const bStr = String(bVal || "").toLowerCase();
            if (sortConfig.direction === "asc") {
                return aStr.localeCompare(bStr);
            }
            return bStr.localeCompare(aStr);
        });
    }, [data, sortConfig.direction, sortConfig.key]);

    const handleSort = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc"
        }));
    }, []);

    const totals = useMemo(() => {
        return {
            hasData: sortedData.length > 0,
            totalLostCount: sortedData.reduce((sum, item) => sum + (item.lost_count || 0), 0),
            totalLostValue: sortedData.reduce((sum, item) => sum + (item.total_lost_value || 0), 0),
        };
    }, [sortedData]);
    const { hasData, totalLostCount, totalLostValue } = totals;

    const COLORS = [
        theme.colors.primary,
        theme.colors.danger,
        "#f59e0b",
        "#10b981",
        "#3b82f6",
        "#8b5cf6",
        "#ec4899",
        "#14b8a6",
    ];

    const getThStyle = useCallback((align) => {
        return {
            textAlign: align,
            padding: "12px 16px",
            borderBottom: "1px solid rgba(148,163,184,0.1)",
            fontWeight: 800,
            color: "#ffffff",
            fontSize: "14px",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            cursor: "pointer",
            userSelect: "none",
            transition: "color 0.2s",
        };
    }, []);

    const getSortIcon = useCallback((key) => {
        if (sortConfig.key !== key) return "";
        return sortConfig.direction === "asc" ? " ↑" : " ↓";
    }, [sortConfig.direction, sortConfig.key]);

    function CustomXAxisTick(props) {
        const { x, y, payload } = props;
        const raw = String(payload.value || "");

        const replacements = {
            "No response from the client": ["No response", "from client"],
            "Constraint ? Project Timeline": ["Constraint", "Project Timeline"],
            "Clients? payment terms": ["Client's", "payment terms"],
            "Time Constraint ? Estimation": ["Time Constraint", "Estimation"],
            "Material Unavailability": ["Material", "Unavailability"],
            "Lost to Competitor": ["Lost to", "Competitor"],
            "Budget Constraints": ["Budget", "Constraints"],
        };

        let lines = [raw];
        Object.keys(replacements).forEach((k) => {
            if (raw.toLowerCase() === k.toLowerCase()) {
                lines = replacements[k];
            }
        });

        if (lines.length === 1 && raw.length > 20) {
            const words = raw.split(" ");
            const half = Math.ceil(words.length / 2);
            lines = [words.slice(0, half).join(" "), words.slice(half).join(" ")];
        }

        return (
            <g transform={`translate(${x},${y + 12})`}>
                <text x={0} y={0} textAnchor="middle" fill="#e2e8f0" fontSize={11} fontWeight={800}>
                    {lines.map((ln, i) => (
                        <tspan key={i} x={0} dy={i === 0 ? 0 : 14}>
                            {ln}
                        </tspan>
                    ))}
                </text>
            </g>
        );
    }

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const dataIndex = sortedData.findIndex(d => d.lost_reason === label);
            const categoryColor = dataIndex !== -1 ? COLORS[dataIndex % COLORS.length] : theme.colors.primary;

            return (
                <div style={{
                    background: "rgba(15, 23, 42, 0.95)",
                    backdropFilter: "blur(12px)",
                    border: `2px solid ${categoryColor}`,
                    borderRadius: 12,
                    padding: "16px",
                    boxShadow: `0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 0 10px ${categoryColor}20`,
                }}>
                    <p style={{ margin: "0 0 8px 0", color: "#fff", fontSize: 16, fontWeight: 800 }}>{label}</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <p style={{ margin: 0, color: categoryColor, fontSize: 14, fontWeight: 700 }}>
                            Lost Count: <span style={{ color: "#fff", fontSize: 18 }}>{payload[0].value}</span>
                        </p>
                        <p style={{ margin: 0, color: categoryColor, fontSize: 14, fontWeight: 700 }}>
                            Lost Value: <span style={{ color: "#fff", fontSize: 18 }}>{formatCurrency(payload[0].payload.total_lost_value, displayCurrency)}</span>
                        </p>
                    </div>
                </div>
            );
        }
        return null;
    };

    const handleMaximize = useCallback(() => {
        if (onMaximize) onMaximize();
    }, [onMaximize]);

    const actionButtons = useMemo(() => (
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {onMaximize && !fullScreen && (
                <button
                    onClick={handleMaximize}
                    style={{
                        padding: "8px",
                        background: "rgba(255, 255, 255, 0.05)",
                        color: "#e5e7eb",
                        borderRadius: 8,
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.2s ease"
                    }}
                    title="Maximize"
                >
                    <span style={{ fontSize: "16px", lineHeight: 1 }}>⤢</span>
                </button>
            )}
            {fullScreen && onMaximize && (
                <button
                    onClick={handleMaximize}
                    style={{
                        padding: "8px",
                        background: "rgba(244, 67, 54, 0.2)",
                        color: "#f44336",
                        border: "1px solid #f44336",
                        borderRadius: "50%",
                        width: "36px",
                        height: "36px",
                        cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.2s ease"
                    }}
                >

                </button>
            )}
        </div>
    ), [onMaximize, fullScreen, handleMaximize]);

    const subtitle = useMemo(() => (
        <div style={{
            color: "#94a3b8",
            fontSize: fullScreen ? "24px" : "18px",
            marginTop: fullScreen ? "0" : "-10px",
            marginBottom: theme.spacing.md,
            fontWeight: 500
        }}>
            {salesperson && <span style={{ color: "#e2e8f0", fontWeight: "bold" }}>{salesperson}</span>}
            {year && <span> · {year}</span>}
            {region && <span> · Region: <strong style={{ color: "#e2e8f0" }}>{region === "all" ? "All Regions" : region}</strong></span>}
        </div>
    ), [salesperson, year, region, fullScreen]);

    const Content = useCallback(() => (
        <div style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing.lg,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            paddingRight: "4px"
        }}>
            {/* Summary Cards */}
            <div style={{ display: "flex", gap: 12, marginBottom: 0, flexWrap: "wrap" }}>
                <SummaryCard
                    title="Total Lost Count"
                    value={totalLostCount}
                    subtitle="opportunities"
                    color={COLORS[0]}
                    glass={true}
                    valueSize="48px"
                />
                <SummaryCard
                    title="Total Lost Value"
                    value={formatCurrency(totalLostValue, displayCurrency)}
                    subtitle={displayCurrency}
                    color={COLORS[1]}
                    glass={true}
                    valueSize="48px"
                />
                <SummaryCard
                    title="Unique Reasons"
                    value={sortedData.length}
                    subtitle="categories"
                    color={COLORS[2]}
                    glass={true}
                    valueSize="48px"
                />
            </div>

            {/* Chart */}
            <div style={{
                width: "100%",
                height: fullScreen ? "40vh" : "300px",
                background: "rgba(15, 23, 42, 0.3)",
                borderRadius: 16,
                padding: theme.spacing.md,
                flex: fullScreen ? "0 0 auto" : "0 0 300px",
                border: "1px solid rgba(255,255,255,0.05)"
            }}>
                <h3 style={{
                    color: "#e5e7eb",
                    fontSize: 22,
                    fontWeight: 600,
                    marginBottom: theme.spacing.sm,
                    marginTop: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                }}>
                    <span style={{ width: "4px", height: "16px", backgroundColor: theme.colors.primary, borderRadius: "2px", display: "inline-block" }}></span>
                    Lost Count by Reason
                </h3>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sortedData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                        <XAxis
                            dataKey="lost_reason"
                            tick={<CustomXAxisTick />}
                            height={80}
                            interval={0}
                            axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                        />
                        <YAxis
                            tick={{ fill: "#ffffff", fontSize: 13, fontWeight: 900 }}
                            axisLine={{ stroke: "rgba(255,255,255,0.2)" }}
                        >
                            <Label value="No of deals" angle={-90} position="insideLeft" offset={0} fill="#cbd5e1" style={{ fontSize: 14, fontWeight: 800 }} />
                        </YAxis>
                        <Tooltip
                            content={<CustomTooltip />}
                            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                        />
                        <Bar dataKey="lost_count" radius={[4, 4, 0, 0]}>
                            {sortedData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* Table */}
            <div style={{
                marginTop: 8,
                flex: fullScreen ? "1 0 auto" : "0 0 auto",
                minHeight: "300px",
                background: "rgba(15, 23, 42, 0.3)",
                borderRadius: 16,
                border: "1px solid rgba(255,255,255,0.05)",
                display: "flex",
                flexDirection: "column",
                overflow: "visible"
            }}>
                <div style={{ overflowX: "auto", overflowY: "auto", flex: 1 }}>
                    <table style={{
                        width: "100%",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        fontSize: 13,
                        color: "#e5e7eb",
                    }}>
                        <thead style={{
                            position: "sticky",
                            top: 0,
                            background: "rgba(15, 23, 42, 0.95)",
                            zIndex: 10,
                            backdropFilter: "blur(4px)"
                        }}>
                            <tr>
                                <th
                                    style={getThStyle("left")}
                                    onClick={() => handleSort("lost_reason")}
                                >
                                    Lost Reason <span style={{ color: theme.colors.primary }}>{getSortIcon("lost_reason")}</span>
                                </th>
                                <th
                                    style={getThStyle("right")}
                                    onClick={() => handleSort("lost_count")}
                                >
                                    Lost Count <span style={{ color: theme.colors.primary }}>{getSortIcon("lost_count")}</span>
                                </th>
                                <th
                                    style={getThStyle("right")}
                                    onClick={() => handleSort("total_lost_value")}
                                >
                                    Total Lost Value <span style={{ color: theme.colors.primary }}>{getSortIcon("total_lost_value")}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData.map((row, idx) => {
                                const rowColor = COLORS[idx % COLORS.length];
                                return (
                                    <tr
                                        key={`reason-${row.lost_reason}-${idx}`}
                                        style={{
                                            backgroundColor: idx % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent",
                                            transition: "background-color 0.15s",
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)"}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent"}
                                    >
                                        <td style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.03)", fontWeight: "500" }} title={row.lost_reason || "Unknown"}>
                                            {row.lost_reason || "Unknown"}
                                        </td>
                                        <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: "900", fontSize: "18px", color: rowColor, borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                                            {row.lost_count || 0}
                                        </td>
                                        <td style={{ padding: "12px 16px", textAlign: "right", color: rowColor, fontWeight: "900", fontSize: "18px", borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                                            {formatCurrency(row.total_lost_value, displayCurrency)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    ), [
        COLORS,
        sortedData,
        theme,
        fullScreen,
        totalLostCount,
        totalLostValue,
        getThStyle,
        getSortIcon,
        handleSort
    ]);

    return (
        <DashboardCard
            title="Closed Lost Reasons"
            actions={actionButtons}
            gradient="danger"
            fullScreen={fullScreen}
        >
            {subtitle}

            {loading && <SkeletonCard height="300px" />}

            {error && (
                <div style={{
                    padding: theme.spacing.lg,
                    background: theme.colors.background.card,
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.colors.danger}`,
                    color: theme.colors.danger,
                }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {!loading && !error && !hasData && (
                <EmptyState
                    icon=""
                    message="No closed lost reasons found for the selected filters."
                    size="medium"
                />
            )}

            {!loading && !error && hasData && <Content />}
        </DashboardCard>
    );
}

export default ClosedLostReasons;
